import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuthStore } from '@/shared/state/authstore'
import { fetchMyInvitations, acceptInvitation, declineInvitation, type AcceptFulfillment } from '../api/invitationApi'
import type { GetMyInvitationsRequest } from './types'

export const invitationKeys = {
  all: ['invitations'] as const,
  list: (params: GetMyInvitationsRequest) => [...invitationKeys.all, 'list', params] as const,
}

export const useMyInvitations = (params: GetMyInvitationsRequest = { status: 'Pending' }) => {
  return useQuery({
    queryKey: invitationKeys.list(params),
    queryFn: () => fetchMyInvitations(params),
    staleTime: 30 * 1000,
  })
}

export const useAcceptInvitation = () => {
  const queryClient = useQueryClient()
  const clearAccessToken = useAuthStore((s) => s.clearAccessToken)

  return useMutation({
    mutationFn: ({ id, fulfillments }: { id: number; fulfillments: AcceptFulfillment[] }) =>
      acceptInvitation(id, fulfillments),
    onSuccess: () => {
      clearAccessToken()
      queryClient.invalidateQueries({ queryKey: invitationKeys.all })
      toast.success('Invitation accepted')
    },
  })
}

export const useDeclineInvitation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => declineInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitationKeys.all })
      toast.success('Invitation declined')
    },
  })
}
